import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Grid, Hidden, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import { useForm } from 'react-hook-form';
import { buscarOrdenesValidacion } from '../../services/Seo/Orden';
import PrestadorSelect from '../Prestador/PrestadorSelect';
import { HighlightOff, Search, CheckCircle, UploadFile } from '@mui/icons-material';
import { getPrestadorLogueado } from '../../services/Busquedas/Prestadores';
import { getConsultorioLogueado } from '../../services/Busquedas/Prestadores';
import PrestadorAutocompleteVirtualizado from '../Prestador/PrestadorAutocompleteVirtualizado';
import BuscarAfiliado from '../Afiliado/BuscarAfiliado';
import { LoadingButton } from '@mui/lab';
import OrdenesValidacionTable from './OrdenesValidacionTable';

/**
 * Componente parametrizado para buscar órdenes
 * @param {Object} props
 * @param {string} props.modo - 'validar' o 'adjuntar' para determinar el comportamiento
 * @param {string} props.titulo - Título de la sección
 * @param {string} props.descripcion - Descripción de la funcionalidad
 * @param {Function} props.onOrdenValidada - Callback cuando una orden es validada
 * @param {Function} props.onDocumentacionCargada - Callback cuando se cargan documentos
 */
const BuscarOrdenesForm = ({ 
  modo = 'validar', // 'validar' o 'adjuntar'
  titulo = 'Validar la orden por QR',
  descripcion = 'Acá podrás informar que la orden se usó para atender al paciente y el sistema validará que está en condiciones de ser cobrada.',
  onOrdenValidada,
  onDocumentacionCargada
}) => {
  const [selectedAfiliado, setSelectedAfiliado] = useState(null);
  const [selectedPrestador, setSelectedPrestador] = useState(null);
  const [showTable, setShowTable] = useState(false);
  const [noResult, setNoResult] = useState(false);

  const [loadingBuscar, setLoadingBuscar] = useState(false);
  const [ordenes, setOrdenes] = useState(null);

  const [successMessage, setSuccessMessage] = useState('');
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [docMessage, setDocMessage] = useState('');
  const [docModalOpen, setDocModalOpen] = useState(false);

  const [prestadorLogueado, setPrestadorLogueado] = useState(null);
  const [consultorioLogueado, setConsultorioLogueado] = useState(null);

  // Valores por defecto para variables que no se modifican en este componente
  const ayudaActivada = false;
  const scanActivo = false;
  const keyComponentSelectPrestador = 0;

  useEffect(() => {
    const getPrestador = async () => {
      const prestador = await getPrestadorLogueado();
      if (prestador != null) {
        setPrestadorLogueado(prestador);
        setSelectedPrestador(prestador);
      } else {
        const consultorio = await getConsultorioLogueado();
        if (consultorio != null) {
          setConsultorioLogueado(consultorio);
        }
      }
    };

    getPrestador();
  }, []);

  const {
    register,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm({ mode: 'onBlur' });

  const handlerLimpiar = () => {
    reset();
    setLoadingBuscar(false);
    setSelectedAfiliado(null);
    setShowTable(false);
    setNoResult(false);
  };

  const onBuscar = async () => {
    try {
      setShowTable(false);

      const res = await buscarOrdenesValidacion(getDataOrden());

      setOrdenes(res);
      res.length > 0 ? setShowTable(true) : setNoResult(true);
      setLoadingBuscar(false);
    } catch (e) {
      setLoadingBuscar(false);
    }
  };

  const onSubmitBuscar = async (data, event) => {
    event.preventDefault();
    setLoadingBuscar(true);
    onBuscar();
  };

  const getDataOrden = () => {
    let data = {
      idPrestador: selectedPrestador.idPrestador,
      idAfiliado: selectedAfiliado.idAfiliado,
      paraValidar: modo === 'validar',
      paraAdjuntarResultados: modo === 'adjuntar',
    };
    return data;
  };

  const validateAfiliado = () => {
    return selectedAfiliado !== null ? true : false;
  };

  const handlerSelectPrestador = (prestador) => {
    setSelectedPrestador(prestador);
  };

  const handleOrdenValidada = () => {
    setSuccessMessage(
      'Si esta orden corresponde a un estudio con informe, recuerde que para evitar débitos deberá subirlo posteriormente desde la opción &quot;Adjuntar Informes de Ordenes&quot;.'
    );
    setSuccessModalOpen(true);
    onOrdenValidada?.();
    onBuscar();
  };

  const handleDocumentacionCargada = () => {
    setDocMessage('Los informes han sido guardados correctamente.');
    setDocModalOpen(true);
    onDocumentacionCargada?.();
    onBuscar();
  };

  const handleCloseSuccessModal = () => {
    setSuccessModalOpen(false);
    setSuccessMessage('');
  };

  const handleCloseDocModal = () => {
    setDocModalOpen(false);
    setDocMessage('');
  };

  const DrawerForm = (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Card sx={{ width: '100%', p: 1 }}>
          <form onSubmit={handleSubmit(onSubmitBuscar)}>
            <CardContent sx={{ p: 2 }}>
              <Hidden xsUp={scanActivo || ayudaActivada} xsDown={scanActivo || ayudaActivada}>
                <Grid container spacing={2} alignItems='center'>
                  <Grid item xs={12}>
                    {prestadorLogueado?.idProfesional || consultorioLogueado?.idConsultorio ? (
                      <PrestadorSelect
                        key={keyComponentSelectPrestador + '_prestador'}
                        value={selectedPrestador}
                        disabled={prestadorLogueado !== null ?? true}
                        name='prestadorRegister'
                        {...register('prestadorRegister', {
                          validate: () => selectedPrestador !== null || false,
                        })}
                        onSelectPrestador={handlerSelectPrestador}
                      />
                    ) : (
                      <PrestadorAutocompleteVirtualizado
                        key={keyComponentSelectPrestador + '_prestador'}
                        value={selectedPrestador}
                        disabled={prestadorLogueado !== null ?? true}
                        name='prestadorRegister'
                        {...register('prestadorRegister', {
                          validate: () => selectedPrestador !== null || false,
                        })}
                        onSelectedPrestador={handlerSelectPrestador}
                      />
                    )}
                  </Grid>
                </Grid>
                {errors.prestadorRegister && errors.prestadorRegister.type === 'validate' && (
                  <p style={{ color: 'red' }}>Debe seleccionar el prestador</p>
                )}
              </Hidden>
              <BuscarAfiliado
                initialValue={selectedAfiliado}
                onSelectAfiliado={(afiliado) => setSelectedAfiliado(afiliado)}
                ref={register('afiliadoRegister', {
                  validate: validateAfiliado,
                })}
              />
              {errors.afiliadoRegister != null && (
                <p style={{ color: 'red' }}>Debe seleccionar un afiliado activo</p>
              )}
            </CardContent>
            <Hidden xsUp={scanActivo || ayudaActivada} xsDown={scanActivo || ayudaActivada}>
              <CardActions sx={{ px: 2 }}>
                <Grid container>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 2,
                      }}
                    >
                      <Button
                        variant='contained'
                        startIcon={<HighlightOff />}
                        color='neutral'
                        onClick={handlerLimpiar}
                      >
                        Limpiar
                      </Button>
                      <LoadingButton
                        variant='contained'
                        size='medium'
                        color='primary'
                        startIcon={<Search />}
                        type='submit'
                        loading={loadingBuscar}
                        disabled={!selectedPrestador || !selectedAfiliado}
                      >
                        Buscar
                      </LoadingButton>
                    </Box>
                  </Grid>
                </Grid>
              </CardActions>
            </Hidden>
          </form>

          {showTable && (
            <Card sx={{ width: '100%' }}>
              <CardContent>
                <OrdenesValidacionTable 
                  ordenes={ordenes}
                  modo={modo}
                  onOrdenValidada={handleOrdenValidada}
                  onDocumentacionCargada={handleDocumentacionCargada} 
                />
              </CardContent>
            </Card>
          )}
          {noResult && <Alert severity='info'>No se encontraron resultados</Alert>}
        </Card>
      </Grid>
    </Grid>
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Card sx={{ width: '100%', p: 0 }}>
          <Hidden xsUp={ayudaActivada} xsDown={ayudaActivada}>
            <CardContent>
              <Typography gutterBottom variant='h5'>
                {titulo}
              </Typography>
              <Typography gutterBottom>
                {descripcion}
              </Typography>
            </CardContent>
          </Hidden>

          {DrawerForm}
        </Card>

        <Dialog open={successModalOpen} onClose={handleCloseSuccessModal} fullWidth maxWidth='sm'>
          <DialogTitle id='alert-dialog-title'>
            <Grid
              container
              direction='row'
              justifyContent='flex-start'
              alignItems='felx-end'
              spacing={1}
            >
              <Grid item>
                {' '}
                <Typography variant='h6'>Orden Validada</Typography>
              </Grid>
              <Grid item>
                {' '}
                <CheckCircle fontSize='small' color='primary'></CheckCircle>
              </Grid>
            </Grid>
          </DialogTitle>
          <DialogContent>
            <Typography>{successMessage}</Typography>
          </DialogContent>
          <DialogActions>
            <Button variant='contained' onClick={handleCloseSuccessModal}>
              Aceptar
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog open={docModalOpen} onClose={handleCloseDocModal} fullWidth maxWidth='sm'>
          <DialogTitle id='doc-alert-dialog-title'>
            <Grid container direction='row' justifyContent='flex-start' alignItems='center' spacing={1}>
              <Grid item>
                <Typography variant='h6'>Documentación cargada</Typography>
              </Grid>
              <Grid item>
                <UploadFile fontSize='small' color='primary' />
              </Grid>
            </Grid>
          </DialogTitle>
          <DialogContent>
            <Typography>{docMessage}</Typography>
          </DialogContent>
          <DialogActions>
            <Button variant='contained' onClick={handleCloseDocModal}>
              Aceptar
            </Button>
          </DialogActions>
        </Dialog>
      </Grid>
    </Grid>
  );
};

export default BuscarOrdenesForm;
